import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, FileText, Archive, Calendar, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import AITakeoffButton from "@/budget/components/AITakeoffButton";
import SearchBar from "@/components/SearchBar";
import Segmented from "@/components/Segmented";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${window.location.origin}/api/budget`;

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  submitted: "bg-green-50 text-green-700 border-green-200",
  archived: "bg-gray-50 text-gray-500 border-gray-200",
};

function formatCurrency(n) {
  if (!n && n !== 0) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return "—"; }
}

export default function Dashboard() {
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("active");
  const restore = async (id) => {
    try { await axios.post(`${API}/budgets/${id}/restore`); toast.success("Budget restored"); fetchBudgets(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not restore"); }
  };
  const navigate = useNavigate();

  const fetchBudgets = async () => {
    try {
      const { data } = await axios.get(`${API}/budgets`, { params: { include_deleted: true } });
      setBudgets(data);
    } catch {
      toast.error("Failed to load budgets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBudgets(); }, []);

  const handleDelete = async () => {
    try {
      const role = localStorage.getItem("abs_role") || "estimator";
      await axios.delete(`${API}/budgets/${deleteId}`, { params: { user_name: role } });
      toast.success("Budget deleted");
      fetchBudgets();
    } catch {
      toast.error("Failed to delete budget");
    } finally {
      setDeleteId(null);
    }
  };

  const q = search.trim().toLowerCase();
  const matchQ = (b) => !q || [b.project_name, b.customer, b.project_info?.customer, b.project_info?.project_name, b.type, b.status, b.budget_number]
    .some((v) => String(v || "").toLowerCase().includes(q));
  const live = budgets.filter((b) => !b.deleted_at);
  const archived = budgets.filter((b) => b.deleted_at && matchQ(b));
  const shown = view === "active" ? live.filter(matchQ) : archived;

  return (
    <div className="flex flex-col gap-3 p-3" data-testid="dashboard">
      {/* Toolbar — its own row above the grid, like the rest of the app */}
      <div className="flex shrink-0 flex-wrap items-center gap-2" data-testid="budgets-toolbar">
        <SearchBar value={search} onChange={setSearch} placeholder="Search..." testId="budgets-search" />
        <Segmented value={view} onChange={setView}
                   options={[{ key: "active", label: `Active ${live.filter(matchQ).length}`, testId: "budgets-view-active" },
                             { key: "archived", label: `Archived ${archived.length}`, testId: "budgets-view-archived" }]} />
        {view === "active" && <><Button size="sm" className="h-7 gap-1 px-2.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => navigate("/budgets/new")} data-testid="new-budget-btn">
          <Plus className="h-3.5 w-3.5" /> Budget
        </Button>
        <AITakeoffButton /></>}
        <span className="ml-auto text-xs font-medium text-slate-400" data-testid="budgets-count">
          {shown.length} budget{shown.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Budget table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="budgets-table">

        {loading ? (
          <div className="py-16 text-center text-slate-400 text-sm">Loading...</div>
        ) : shown.length === 0 ? (
          <div className="py-20 text-center" data-testid="empty-state">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">{view === "archived" ? "Nothing archived" : "No budgets yet"}</p>
            <p className="text-slate-400 text-sm mt-1">Create your first budget to get started</p>
            <Button onClick={() => navigate("/budgets/new")} className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-2" data-testid="empty-new-budget-btn">
              <Plus className="w-4 h-4" /> New Budget
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-[11px] text-muted-foreground">
                  <th className="text-left px-5 py-2 font-semibold">Name</th>
                  <th className="text-left px-4 py-2 font-semibold">Prepared By</th>
                  <th className="text-left px-4 py-2 font-semibold hidden md:table-cell">Location</th>
                  <th className="text-left px-4 py-2 font-semibold">Status</th>
                  <th className="text-left px-4 py-2 font-semibold hidden md:table-cell">Scopes</th>
                  <th className="text-right px-4 py-2 font-semibold">Total</th>
                  <th className="text-left px-4 py-2 font-semibold hidden md:table-cell">Last Modified</th>
                  <th className="px-4 py-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((b, idx) => (
                  <tr
                    key={b.id}
                    onClick={() => navigate(`/budgets/${b.id}`)}
                    className={`border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer ${view === "archived" ? "opacity-80" : ""}`}
                    data-testid={`budget-row-${idx}`}
                  >
                    <td className="px-5 py-3.5">
                      <div className="font-medium text-slate-900">{b.project_info?.project_name || "Untitled Budget"}</div>
                      {b.project_info?.contact && <div className="text-xs text-slate-400 mt-0.5">{b.project_info.contact}</div>}
                    </td>
                    <td className="px-4 py-3.5 text-slate-500 text-xs" data-testid={`budget-prepared-by-${idx}`}>
                      {b.project_info?.prepared_by || "—"}
                    </td>
                    <td className="px-4 py-3.5 hidden md:table-cell">
                      <div className="flex items-center gap-1 text-slate-500 text-xs">
                        {b.project_info?.site_location && <MapPin className="w-3 h-3" />}
                        {b.project_info?.site_location || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <Badge className={`text-xs rounded-sm border ${STATUS_COLORS[b.status] || STATUS_COLORS.draft}`} data-testid={`budget-status-${idx}`}>
                        {b.status || "draft"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3.5 hidden md:table-cell text-slate-500 text-xs">
                      {b.selected_scopes?.length || 0} scope{b.selected_scopes?.length !== 1 ? "s" : ""}
                    </td>
                    <td className="px-4 py-3.5 text-right font-medium text-slate-900 td-num" data-testid={`budget-total-${idx}`}>
                      {formatCurrency(b.grand_total)}
                    </td>
                    <td className="px-4 py-3.5 hidden md:table-cell text-slate-400 text-xs">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(b.updated_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3.5" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {view === "active" ? (
                          <button type="button" onClick={() => setDeleteId(b.id)} title="Archive" data-testid={`delete-budget-${idx}`}
                                  className="rounded p-1 text-slate-400 transition-colors hover:bg-amber-50 hover:text-amber-700"><Archive className="h-3.5 w-3.5" /></button>
                        ) : (
                          <button type="button" onClick={() => restore(b.id)} className="text-xs text-[#007BFF] hover:underline" data-testid={`budget-restore-${idx}`}>Restore</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Budget?</AlertDialogTitle>
            <AlertDialogDescription>The budget will be moved to the Archive. An admin can restore it later.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
